import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

const PAGE_SIZE = 100;

// A dose creates two messages per action, and an action is create, correct or cancel,
// so six rows is the realistic ceiling for one dose. The endpoint pages by patient
// rather than by immunisation, so the whole patient's history has to be walked to be
// sure none of a dose's rows sit past a page boundary. This caps that walk: a patient
// with more than 2000 vaccine messages is a data problem, not a page to render.
const MAX_PAGES = 20;

const byNewestFirst = (first, second) => {
  const firstDate = first?.submissionDate || first?.created || "";
  const secondDate = second?.submissionDate || second?.created || "";

  return secondDate.localeCompare(firstDate);
};

/**
 * Every Healthlink message sent for one immunisation, newest first.
 *
 * The backend's `/healthlink-status` filters by patient and by status, never by
 * immunisation — noted as a known caveat on KIN-1826 — so the patient's messages are
 * fetched and grouped here on `patientImmunisationId`. Paging is followed to the end
 * rather than trusting one page: a dose that has been corrected and cancelled has six
 * rows, and there is no ordering guarantee that keeps them together.
 *
 * This is the per-message granularity, the one that says what each transmission
 * actually did. The collapsed `status` and the per-receiver `niis`/`pcrs` blocks both
 * come down on the immunisation itself and need no request.
 */
export const useHealthlinkVaccineMessages = (
  patientId,
  immunisationId,
  enabled = true
) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["healthlinkVaccineMessages", tenantVal, patientId, immunisationId],
    enabled: !!enabled && !!tenantVal && !!patientId && !!immunisationId,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const collected = [];

      for (let page = 0; page < MAX_PAGES; page += 1) {
        const response = await axiosInstance.get(
          `${tenantVal}/healthlink/vaccine/healthlink-status`,
          { params: { patientId, page, size: PAGE_SIZE } }
        );

        const transactions = response?.data?.transactions;

        if (!Array.isArray(transactions) || transactions.length === 0) break;

        collected.push(...transactions);

        const totalElements = response?.data?.totalElements;
        const isLastPage =
          transactions.length < PAGE_SIZE ||
          (Number.isFinite(totalElements) && collected.length >= totalElements);

        if (isLastPage) break;
      }

      return collected
        .filter((transaction) => transaction?.patientImmunisationId === immunisationId)
        .sort(byNewestFirst);
    },
  });
};
